<script setup lang="ts">
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useEventListener } from "@vueuse/core";
import { setupCuloriModes } from "ls/components/color/utils";
import { ConfigProvider } from "reka-ui";
import { defineAsyncComponent } from "vue";
import { isElectron, isElectronRenderer } from "@/common/environment";
import { cspNonce } from "@/csp";
import { useChunkPreloadErrorHandling } from "./chunk-reload";
import AppStartFailed from "./components/AppStartFailed.vue";
import { useProvideRecaptchaContext } from "./components/recaptcha/useRecaptchaContext";
import { SimpleToaster } from "./components/simple-toast";
import { SimpleTooltipProvider } from "./components/simple-tooltip";
import { useIsEmbed } from "./composables/useIsEmbed";
import { usePointer } from "./composables/usePointer";
import RpLicenseAuditDialog from "./features/access-control/RpLicenseAuditDialog.vue";
import SubInstanceRpAccessDialog from "./features/access-control/SubInstanceRpAccessDialog.vue";
import ViewOnlyFilesystemDialog from "./features/access-control/ViewOnlyFilesystemDialog.vue";
import AiChatModal from "./features/ai-chat/AiChatModal.vue";
import Banners from "./features/banner/Banners.vue";
import ClickthroughUploadProgress from "./features/clickthrough/ClickthroughUploadProgress.vue";
import CommandK from "./features/command-k/CommandK.vue";
import { useContextMenuGuard } from "./features/context-menu/useContextMenuGuard";
import AxureCloudDevTools from "./features/dev-tools/AxureCloudDevTools.vue";
import { useDiskoDotnetRuntimeGuard } from "./features/disko/useDiskoDotnetRuntime";
import OnlineProjectPermissionDialog from "./features/files/permissions-dialogs/OnlineProjectPermissionDialog.vue";
import GlobalHubs from "./features/hubs/GlobalHubs.vue";
import { useTimezoneAutoDetection } from "./features/settings/useTimezones";
import { useAuth } from "./features/user/useAuth";
import { useAuthGuard } from "./router/auth-guard";
import { useEmbedGuard } from "./router/embed-guard";
import { useConfigureRoutes } from "./router/useConfigureRoutes";
import TitleBar from "./TitleBar.vue";
import "./style.css";
import "./proseMirror.css";

const DesktopAppFeatures = defineAsyncComponent(() => import("./features/desktop-app/DesktopAppFeatures.vue"));

useProvideRecaptchaContext();
useChunkPreloadErrorHandling();
useTimezoneAutoDetection();
useConfigureRoutes();
useAuthGuard();
useEmbedGuard();
setupCuloriModes();

const { user } = useAuth();

const isEmbed = useIsEmbed();

useContextMenuGuard();
useDiskoDotnetRuntimeGuard();

// Prevent pinch-zoom on coarse pointer (touch) devices
const { isCoarse } = usePointer();
useEventListener("wheel", event => {
  if (isCoarse.value && (event.ctrlKey || event.metaKey)) {
    event.preventDefault();
  }
}, { passive: false });
</script>

<template>
  <ConfigProvider :nonce="cspNonce">
    <SimpleTooltipProvider>
      <div
        class="flex size-full flex-col"
      >
        <TitleBar v-if="isElectron" />
        <Banners />
        <RouterView />
        <AppStartFailed />
      </div>
      <SimpleToaster />

      <template v-if="user">
        <div data-axure-sidebar-teleport-target />
        <CommandK v-if="!isEmbed" />
        <ClickthroughUploadProgress />
        <GlobalHubs />
        <RpLicenseAuditDialog />
        <SubInstanceRpAccessDialog />
        <OnlineProjectPermissionDialog />
        <ViewOnlyFilesystemDialog />
        <AiChatModal />
      </template>

      <DesktopAppFeatures v-if="isElectronRenderer" />

      <AxureCloudDevTools />
      <VueQueryDevtools :style-nonce="cspNonce" />
    </SimpleTooltipProvider>
  </ConfigProvider>
</template>
